import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import tinycolor from "tinycolor2";
import InventoryTable from "../components/admin/inventory/InventoryTable";
import InventoryGrid from "../components/admin/inventory/InventoryGrid";
import SearchInput from "../components/CommonComponents/SearchInput";
import ReactSelectField from "../components/CommonComponents/ReactSelectField";
import ViewToggle from "../components/CommonComponents/ViewToggle";
import EmptyState from "../components/CommonComponents/EmptyState";
import ProductImagePreviewModal from "../components/admin/products/ProductImagePreviewModal";
import { getInventory, updateInventory } from "../services/inventoryServices";
import { fetchBranches } from "../services/branchApi";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { resolveApiUrl } from "../utils/apiBaseUrl";
import { AlertCircle, ArrowUpDown, Building2, ChevronDown, Clipboard, FileText, ListFilter, Package, PlusCircle, Search, Share2, ShoppingBag, Trash2, Warehouse, X } from "lucide-react";
import EditStockModal from "../components/admin/inventory/EditStockModal";

const LOW_STOCK_THRESHOLD = parseInt(process.env.REACT_APP_LOW_STOCK_THRESHOLD, 10) || 10;
const RESTOCK_STORAGE_PREFIX = "inventory_restock_checklist";
const stockFilterOptions = [
    { value: "all", label: "All Items" },
    { value: "low", label: "Low Stock" },
    { value: "out", label: "Out of Stock" },
    { value: "healthy", label: "Healthy Stock" },
];
const stockSortOptions = [
    { value: "stock-asc", label: "Lowest First" },
    { value: "stock-desc", label: "Highest First" },
    { value: "name-asc", label: "Name A-Z" },
    { value: "name-desc", label: "Name Z-A" },
];

const formatDateLabel = (date = new Date()) =>
    date.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" });

const getScopedStock = (item, selectedBranchId) =>
    selectedBranchId ? Number(item.released_quantity || 0) : Number(item.quantity || 0);

const getWarehouseStock = (item) => Number(item.quantity || 0);

const getChecklistActionLabel = (item, selectedBranchId) => {
    const warehouseStock = getWarehouseStock(item);
    const scopedStock = getScopedStock(item, selectedBranchId);

    if (selectedBranchId && scopedStock <= LOW_STOCK_THRESHOLD && warehouseStock > LOW_STOCK_THRESHOLD) {
        return "Transfer";
    }

    if (warehouseStock <= 0) {
        return selectedBranchId && scopedStock > LOW_STOCK_THRESHOLD ? "Reorder" : "Urgent";
    }

    if (warehouseStock <= LOW_STOCK_THRESHOLD) {
        return selectedBranchId && scopedStock > LOW_STOCK_THRESHOLD ? "Reorder" : "Soon";
    }

    return "Optional";
};

const getSuggestedBuyQty = (item) => {
    const warehouseStock = getWarehouseStock(item);
    return Math.max(1, LOW_STOCK_THRESHOLD - warehouseStock);
};

const getThemePrimaryColor = () => {
    if (typeof window === "undefined") return "#0f766e";
    const value = getComputedStyle(document.documentElement).getPropertyValue("--color-primary").trim();
    return tinycolor(value).isValid() ? tinycolor(value).toHexString() : "#0f766e";
};

const buildRestockText = ({ items, branchLabel, selectedBranchId }) => {
    const lines = [
        `To-Buy Checklist - ${branchLabel}`,
        formatDateLabel(),
        "",
    ];

    items.forEach(({ item, buyQty }, index) => {
        const currentStock = getScopedStock(item, selectedBranchId);
        const warehouseStock = getWarehouseStock(item);
        const priority = getChecklistActionLabel(item, selectedBranchId);
        lines.push(`[ ] ${index + 1}. ${item.product_name} - Buy: ${buyQty || "___"} ${item.unit_type || "units"}`);
        lines.push(`    ${branchLabel}: ${currentStock} | Warehouse: ${warehouseStock} | Buy priority: ${priority}`);
    });

    return lines.join("\n");
};

const serializeRestockItems = (itemsById) =>
    Object.entries(itemsById).map(([itemId, entry]) => ({
        itemId,
        buyQty: Number(entry?.buyQty) || 0,
        item: entry?.item
            ? {
                id: entry.item.id,
                product_id: entry.item.product_id,
                product_name: entry.item.product_name,
                unit_type: entry.item.unit_type,
                quantity: entry.item.quantity,
                released_quantity: entry.item.released_quantity,
                image_url: entry.item.image_url,
            }
            : null,
    }));

const deserializeRestockItems = (value) => {
    try {
        const rows = JSON.parse(value || "[]");
        if (!Array.isArray(rows)) return {};
        return rows.reduce((acc, row) => {
            if (!row?.itemId || !row?.item) return acc;
            acc[String(row.itemId)] = {
                item: row.item,
                buyQty: Number(row.buyQty) || 0,
            };
            return acc;
        }, {});
    } catch {
        return {};
    }
};

const InventorySkeleton = ({ viewMode = "table" }) => (
    <div className="animate-pulse space-y-6">
        <div className="rounded-[28px] border border-white/60 bg-[linear-gradient(135deg,rgba(255,255,255,0.95),rgba(248,250,252,0.92))] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:p-6">
            <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-3">
                    <div className="h-4 w-32 rounded-full bg-slate-200" />
                    <div className="h-10 w-60 rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
                    <div className="h-4 w-72 rounded-full bg-slate-100" />
                </div>
                <div className="h-12 w-full max-w-md rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, idx) => (
                    <div key={idx} className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
                        <div className="h-3 w-20 rounded-full bg-slate-100" />
                        <div className="mt-3 h-7 w-16 rounded-full bg-slate-200" />
                    </div>
                ))}
            </div>
        </div>

        <div className="rounded-[26px] border border-slate-200 bg-white/85 p-3 shadow-sm">
            <div className="flex gap-2 overflow-hidden">
                {Array.from({ length: 4 }).map((_, idx) => (
                    <div key={idx} className="h-11 w-32 rounded-2xl bg-slate-100" />
                ))}
            </div>
        </div>

        {viewMode === "table" ? (
            <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white/90 shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
                <div className="grid grid-cols-[minmax(240px,2.4fr),120px,140px,140px,120px] gap-4 border-b border-slate-200 bg-slate-100/90 px-6 py-4">
                    {Array.from({ length: 5 }).map((_, idx) => (
                        <div key={idx} className="h-3.5 rounded-full bg-slate-200" />
                    ))}
                </div>
                <div className="divide-y divide-slate-100">
                    {Array.from({ length: 6 }).map((_, rowIdx) => (
                        <div key={rowIdx} className="grid grid-cols-[minmax(240px,2.4fr),120px,140px,140px,120px] items-center gap-4 px-6 py-5">
                            <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-2xl bg-slate-100" />
                                <div className="min-w-0 flex-1 space-y-2">
                                    <div className="h-4 w-40 rounded-full bg-slate-200" />
                                    <div className="h-3 w-56 max-w-full rounded-full bg-slate-100" />
                                </div>
                            </div>
                            <div className="flex justify-center">
                                <div className="h-9 w-20 rounded-full bg-slate-100" />
                            </div>
                            <div className="flex justify-center">
                                <div className="h-10 w-24 rounded-2xl bg-emerald-50" />
                            </div>
                            <div className="flex justify-center">
                                <div className="h-10 w-24 rounded-2xl bg-amber-50" />
                            </div>
                            <div className="flex justify-center gap-2">
                                <div className="h-8 w-8 rounded-xl bg-slate-100" />
                                <div className="h-8 w-8 rounded-xl bg-slate-200" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
                {Array.from({ length: 12 }).map((_, idx) => (
                    <div key={idx} className="overflow-hidden rounded-[26px] border border-slate-200 bg-white/90 p-4 shadow-[0_18px_40px_rgba(15,23,42,0.07)]">
                        <div className="relative overflow-hidden rounded-2xl bg-slate-100">
                            <div className="h-32 w-full bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100" />
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="h-4 w-4/5 rounded-full bg-slate-200" />
                            <div className="h-3 w-1/2 rounded-full bg-slate-100" />
                            <div className="h-6 rounded-full bg-emerald-50" />
                        </div>
                        <div className="mt-4 flex justify-end">
                            <div className="h-8 w-8 rounded-xl bg-slate-200" />
                        </div>
                    </div>
                ))}
            </div>
        )}

        <div className="mt-4 flex flex-col items-center gap-3">
            <div className="h-9 w-56 rounded-xl bg-slate-100" />
            <div className="h-4 w-60 rounded-full bg-slate-100" />
        </div>
    </div>
);

const InventoryPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [inventories, setInventories] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [branches, setBranches] = useState([]);
    const [selectedBranchId, setSelectedBranchId] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState("table");
    const [showSummaryCards, setShowSummaryCards] = useState(true);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedInventory, setSelectedInventory] = useState(null);
    const [previewProduct, setPreviewProduct] = useState(null);
    const [restockItemsById, setRestockItemsById] = useState({});
    const [restockStorageReadyKey, setRestockStorageReadyKey] = useState(null);
    const [showRestockChecklist, setShowRestockChecklist] = useState(false);
    const [stockFilter, setStockFilter] = useState("all");
    const [stockSort, setStockSort] = useState("stock-asc");
    const inventoriesPerPage = 10;
    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("customer_id");
    const uploadsBaseURL = resolveApiUrl("/uploads");
    const notificationProductId = searchParams.get("productId");
    const notificationProductName = searchParams.get("productName");
    const restockStorageKey = `${RESTOCK_STORAGE_PREFIX}:${customerId || "guest"}:${selectedBranchId || "warehouse"}`;

    const branchTabs = useMemo(() => ([
        { id: "", label: "Warehouse", type: "warehouse" },
        ...branches.map((branch) => ({
            id: String(branch.id),
            label: branch.name || branch.location || `Branch ${branch.id}`,
            type: "branch",
        })),
    ]), [branches]);
    const shouldShowBranchTabs = branches.length > 0;
    const hasNoActiveBranches = branches.length === 0;

    const loadInventory = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getInventory(token, selectedBranchId);
            setInventories(data || []);
        } catch (err) {
            console.error(err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [token, selectedBranchId]);

    const loadBranches = useCallback(async () => {
        try {
            const res = await fetchBranches(customerId);
            const branchList = res.data?.data || res.data || [];
            setBranches(
                Array.isArray(branchList)
                    ? branchList.filter((branch) => String(branch?.status || "").toUpperCase() === "ACTIVE")
                    : []
            );
        } catch (err) {
            console.error(err);
            setBranches([]);
        }
    }, [customerId]);

    const handleAdjust = useCallback(async (productId, change) => {
        if (!token) return showErrorToast("JWT token missing. Please login.");
        try {
            await updateInventory(productId, change, token, selectedBranchId);
            showSuccessToast(`Inventory ${change > 0 ? "restocked" : "deducted"}`);
            loadInventory();
        } catch (err) {
            console.error(err);
            showErrorToast(err.message || "Failed to update inventory");
        }
    }, [token, selectedBranchId, loadInventory]);

    const filteredInventory = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        const matchesFilter = (item) => {
            const stock = getScopedStock(item, selectedBranchId);
            if (stockFilter === "out") return stock <= 0;
            if (stockFilter === "low") return stock > 0 && stock <= LOW_STOCK_THRESHOLD;
            if (stockFilter === "healthy") return stock > LOW_STOCK_THRESHOLD;
            return true;
        };

        return inventories
            .filter((inv) => {
                const productName = String(inv.product_name || "").toLowerCase();
                return (!normalizedSearch || productName.includes(normalizedSearch)) && matchesFilter(inv);
            })
            .sort((a, b) => {
                const aStock = getScopedStock(a, selectedBranchId);
                const bStock = getScopedStock(b, selectedBranchId);
                if (stockSort === "stock-desc") return bStock - aStock || a.product_name.localeCompare(b.product_name);
                if (stockSort === "name-asc") return a.product_name.localeCompare(b.product_name);
                if (stockSort === "name-desc") return b.product_name.localeCompare(a.product_name);
                return aStock - bStock || a.product_name.localeCompare(b.product_name);
            });
    }, [inventories, searchTerm, selectedBranchId, stockFilter, stockSort]);
    const outOfStockCount = useMemo(
        () => inventories.filter((item) => getScopedStock(item, selectedBranchId) <= 0).length,
        [inventories, selectedBranchId]
    );
    const lowStockCount = useMemo(
        () => inventories.filter((item) => {
            const stock = getScopedStock(item, selectedBranchId);
            return stock > 0 && stock <= LOW_STOCK_THRESHOLD;
        }).length,
        [inventories, selectedBranchId]
    );
    const inventorySuggestions = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (!normalizedSearch) return [];
        const uniqueNames = new Set();

        return inventories
            .map((inv) => inv.product_name)
            .filter((name) => {
                if (!name || uniqueNames.has(name)) return false;
                const matches = name.toLowerCase().includes(normalizedSearch);
                if (matches) {
                    uniqueNames.add(name);
                }
                return matches;
            })
            .slice(0, 6);
    }, [inventories, searchTerm]);
    const totalPages = Math.max(Math.ceil(filteredInventory.length / inventoriesPerPage), 1);
    const currentInventory = filteredInventory.slice(
        (currentPage - 1) * inventoriesPerPage,
        currentPage * inventoriesPerPage
    );
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * inventoriesPerPage;
    const activeBranchLabel = hasNoActiveBranches
        ? "No Branch is Active"
        : selectedBranchId
        ? branchTabs.find((tab) => tab.id === selectedBranchId)?.label || "Selected Branch"
        : "Warehouse";
    const totalReleasedStock = hasNoActiveBranches
        ? 0
        : filteredInventory.reduce((sum, inv) => sum + (Number(inv.released_quantity) || 0), 0);
    const selectedRestockIds = useMemo(() => new Set(Object.keys(restockItemsById)), [restockItemsById]);
    const restockItems = useMemo(
        () => Object.values(restockItemsById).sort((a, b) => {
            const priorityOrder = { Urgent: 0, Soon: 1, Reorder: 2, Transfer: 3, Optional: 4 };
            const priorityDiff = priorityOrder[getChecklistActionLabel(a.item, selectedBranchId)] - priorityOrder[getChecklistActionLabel(b.item, selectedBranchId)];
            if (priorityDiff !== 0) return priorityDiff;
            return a.item.product_name.localeCompare(b.item.product_name);
        }),
        [restockItemsById, selectedBranchId]
    );
    const restockText = useMemo(
        () => buildRestockText({ items: restockItems, branchLabel: activeBranchLabel, selectedBranchId }),
        [restockItems, activeBranchLabel, selectedBranchId]
    );

    const toggleRestockItem = useCallback((item) => {
        const itemId = String(item.id);
        setRestockItemsById((prev) => {
            if (prev[itemId]) {
                const next = { ...prev };
                delete next[itemId];
                return next;
            }

            return {
                ...prev,
                [itemId]: {
                    item,
                    buyQty: getSuggestedBuyQty(item),
                },
            };
        });
    }, []);

    const addLowStockToRestockList = useCallback(() => {
        const lowItems = filteredInventory.filter((item) => getWarehouseStock(item) <= LOW_STOCK_THRESHOLD);
        if (!lowItems.length) {
            showErrorToast("No warehouse-low items in the current view.");
            return;
        }

        setRestockItemsById((prev) => {
            const next = { ...prev };
            lowItems.forEach((item) => {
                const itemId = String(item.id);
                if (!next[itemId]) {
                    next[itemId] = {
                        item,
                        buyQty: getSuggestedBuyQty(item),
                    };
                }
            });
            return next;
        });
        showSuccessToast("Warehouse-low items added to the to-buy list.");
    }, [filteredInventory]);

    useEffect(() => {
        const savedRestockItems = deserializeRestockItems(localStorage.getItem(restockStorageKey));
        setRestockItemsById(savedRestockItems);
        setRestockStorageReadyKey(restockStorageKey);
    }, [restockStorageKey]);

    useEffect(() => {
        if (restockStorageReadyKey !== restockStorageKey) return;
        localStorage.setItem(restockStorageKey, JSON.stringify(serializeRestockItems(restockItemsById)));
    }, [restockItemsById, restockStorageKey, restockStorageReadyKey]);

    useEffect(() => {
        if (!inventories.length || !Object.keys(restockItemsById).length) return;

        setRestockItemsById((prev) => {
            let changed = false;
            const next = { ...prev };
            Object.entries(prev).forEach(([itemId, entry]) => {
                const liveItem = inventories.find((item) => String(item.id) === itemId || String(item.product_id) === itemId);
                if (liveItem && liveItem !== entry.item) {
                    next[itemId] = { ...entry, item: liveItem };
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [inventories, restockItemsById]);

    const updateRestockQty = useCallback((itemId, value) => {
        const numericValue = Math.max(0, Number(value) || 0);
        setRestockItemsById((prev) => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                buyQty: numericValue,
            },
        }));
    }, []);

    const copyRestockText = useCallback(async () => {
        if (!restockItems.length) return;
        try {
            await navigator.clipboard.writeText(restockText);
            showSuccessToast("To-buy checklist copied.");
        } catch (error) {
            console.error(error);
            showErrorToast("Could not copy the checklist.");
        }
    }, [restockItems.length, restockText]);

    const createRestockPdfDocument = useCallback(async () => {
        const { default: jsPDF } = await import("jspdf");
        const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
        const primary = getThemePrimaryColor();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 36;
        const contentWidth = pageWidth - margin * 2;
        const addHeader = (isFirstPage = false) => {
            const headerY = isFirstPage ? 30 : 24;
            doc.setFillColor("#f8fafc");
            doc.roundedRect(margin, headerY, contentWidth, 52, 10, 10, "F");
            doc.setDrawColor("#e2e8f0");
            doc.roundedRect(margin, headerY, contentWidth, 52, 10, 10, "S");
            doc.setFillColor(primary);
            doc.roundedRect(margin, headerY, 5, 52, 3, 3, "F");
            doc.setTextColor("#0f172a");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(15);
            doc.text("To-Buy Checklist", margin + 18, headerY + 22);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor("#475569");
            doc.text(`${activeBranchLabel} | ${formatDateLabel()} | ${restockItems.length} item${restockItems.length === 1 ? "" : "s"}`, margin + 18, headerY + 38);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(primary);
            doc.text("ZEROONE POS", pageWidth - margin - 72, headerY + 22);
            return headerY + 72;
        };
        let y = addHeader(true);

        const addFooter = () => {
            doc.setDrawColor("#e2e8f0");
            doc.line(margin, pageHeight - 34, pageWidth - margin, pageHeight - 34);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor("#64748b");
            doc.text("Bring this list when buying supplies. Check items after purchase.", margin, pageHeight - 20);
        };

        const ensureSpace = (neededHeight) => {
            if (y + neededHeight <= pageHeight - 46) return;
            addFooter();
            doc.addPage();
            y = addHeader(false);
        };
        const addChecklistCheckbox = (fieldName, x, yPosition, size = 12) => {
            if (!doc.AcroForm?.CheckBox || typeof doc.addField !== "function") return;
            try {
                const checkbox = new doc.AcroForm.CheckBox();
                checkbox.fieldName = fieldName;
                checkbox.x = x;
                checkbox.y = yPosition;
                checkbox.width = size;
                checkbox.height = size;
                checkbox.value = "Off";
                checkbox.defaultValue = "Off";
                checkbox.appearanceState = "Off";
                checkbox.fontSize = 8;
                checkbox.maxFontSize = 8;
                checkbox.showWhenPrinted = true;
                doc.addField(checkbox);
            } catch (error) {
                console.warn("Could not add interactive PDF checkbox.", error);
            }
        };

        doc.setFont("helvetica", "normal");
        restockItems.forEach(({ item, buyQty }, index) => {
            const currentStock = getScopedStock(item, selectedBranchId);
            const warehouseStock = getWarehouseStock(item);
            const priority = getChecklistActionLabel(item, selectedBranchId);
            const stockLabel = selectedBranchId ? "Branch stock" : "Current stock";
            const itemLines = doc.splitTextToSize(item.product_name, contentWidth - 186);
            const detailLine = `${stockLabel}: ${currentStock}   |   Warehouse: ${warehouseStock}   |   Priority: ${priority}`;
            const detailLines = doc.splitTextToSize(detailLine, contentWidth - 86);
            const rowHeight = Math.max(46, 24 + itemLines.length * 11 + detailLines.length * 10);

            ensureSpace(rowHeight + 8);

            doc.setFillColor("#ffffff");
            doc.setDrawColor("#e2e8f0");
            doc.roundedRect(margin, y, contentWidth, rowHeight, 8, 8, "FD");

            doc.setFillColor(primary);
            doc.circle(margin + 19, y + 18, 9, "F");
            doc.setTextColor("#ffffff");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(7);
            doc.text(String(index + 1), margin + 19, y + 20.5, { align: "center" });

            doc.setTextColor("#0f172a");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text(itemLines, margin + 38, y + 16);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor("#475569");
            doc.text(detailLines, margin + 38, y + 31 + (itemLines.length - 1) * 11);

            doc.setTextColor("#0f172a");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text(`Buy: ${buyQty || "-"} pcs`, pageWidth - margin - 104, y + 20);

            doc.setDrawColor("#0f172a");
            const checkboxX = pageWidth - margin - 30;
            const checkboxY = y + 10;
            doc.rect(checkboxX, checkboxY, 14, 14, "S");
            addChecklistCheckbox(`restock_item_${index + 1}`, checkboxX + 1, checkboxY + 1, 12);
            doc.setDrawColor("#cbd5e1");
            doc.line(margin + 38, y + rowHeight - 8, pageWidth - margin - 48, y + rowHeight - 8);

            y += rowHeight + 6;
        });

        addFooter();
        return doc;
    }, [activeBranchLabel, restockItems, selectedBranchId]);

    const getRestockPdfFileName = useCallback(
        () => `to-buy-checklist-${new Date().toISOString().slice(0, 10)}.pdf`,
        []
    );

    const shareRestockText = useCallback(async () => {
        if (!restockItems.length) return;

        if (!navigator.share) {
            await copyRestockText();
            showSuccessToast("PDF sharing is not available here, so the checklist text was copied.");
            return;
        }

        try {
            const doc = await createRestockPdfDocument();
            const pdfBlob = doc.output("blob");
            const pdfFile = new File([pdfBlob], getRestockPdfFileName(), { type: "application/pdf" });
            const shareData = {
                title: `To-Buy Checklist - ${activeBranchLabel}`,
                text: "To-buy checklist PDF attached.",
                files: [pdfFile],
            };

            if (navigator.canShare?.({ files: [pdfFile] })) {
                await navigator.share(shareData);
                return;
            }

            await navigator.share({
                title: `To-Buy Checklist - ${activeBranchLabel}`,
                text: restockText,
            });
            showSuccessToast("This browser cannot share PDF files, so the text checklist was shared instead.");
        } catch (error) {
            if (error?.name !== "AbortError") {
                console.error(error);
                showErrorToast("Could not open share options.");
            }
        }
    }, [activeBranchLabel, copyRestockText, createRestockPdfDocument, getRestockPdfFileName, restockItems.length, restockText]);

    const downloadRestockPdf = useCallback(async () => {
        if (!restockItems.length) return;

        const doc = await createRestockPdfDocument();
        doc.save(getRestockPdfFileName());
    }, [createRestockPdfDocument, getRestockPdfFileName, restockItems.length]);

    useEffect(() => { loadBranches(); }, [loadBranches]);
    useEffect(() => { loadInventory(); }, [loadInventory]);
    useEffect(() => { if (currentPage > totalPages) setCurrentPage(1); }, [filteredInventory, totalPages, currentPage]);
    useEffect(() => { setCurrentPage(1); }, [selectedBranchId, stockFilter, stockSort, searchTerm]);
    useEffect(() => {
        if (branches.length > 0) {
            const hasSelectedBranch = branches.some((branch) => String(branch.id) === String(selectedBranchId));
            if (selectedBranchId && !hasSelectedBranch) {
                setSelectedBranchId("");
            }
        }

        if (branches.length === 0) {
            if (selectedBranchId) {
                setSelectedBranchId("");
            }
        }
    }, [branches, selectedBranchId]);

    useEffect(() => {
        if (!notificationProductId && !notificationProductName) return;

        const normalizedParamName = notificationProductName?.trim() || "";
        const matchedInventory = notificationProductId
            ? inventories.find((inv) => String(inv.id) === String(notificationProductId) || String(inv.product_id) === String(notificationProductId))
            : null;
        const nextSearchTerm = normalizedParamName || matchedInventory?.product_name || "";

        if (nextSearchTerm) {
            setSearchTerm((prev) => (prev === nextSearchTerm ? prev : nextSearchTerm));
            setCurrentPage(1);
        }

        if (notificationProductId || notificationProductName) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete("productId");
            nextParams.delete("productName");
            setSearchParams(nextParams, { replace: true });
        }
    }, [inventories, notificationProductId, notificationProductName, searchParams, setSearchParams]);

    const handleEditStock = useCallback(async (inventory, newQuantity) => {
        if (!token) return showErrorToast("JWT token missing. Please login.");
        try {
            const currentScopedQuantity = selectedBranchId
                ? (Number(inventory.released_quantity) || 0)
                : (Number(inventory.quantity) || 0);
            const quantityChange = newQuantity - currentScopedQuantity;
            if (quantityChange === 0) return;
            await updateInventory(inventory.id, quantityChange, token, selectedBranchId);
            showSuccessToast("Inventory updated");
            loadInventory();
        } catch (err) {
            console.error(err);
            showErrorToast(err.message || "Failed to update inventory");
        }
    }, [token, selectedBranchId, loadInventory]);

    const handleSaveStock = (newQuantity) => {
        if (selectedInventory) {
            handleEditStock(selectedInventory, newQuantity);
        }
        setEditModalOpen(false);
        setSelectedInventory(null);
    };

    const handleCloseModal = () => {
        setEditModalOpen(false);
        setSelectedInventory(null);
    };

    return (
        <div className="pt-3">
            <div className="flex flex-col gap-4">
                <section className="relative overflow-hidden rounded-[30px] border border-slate-200/85 bg-[linear-gradient(145deg,#fffafa,#fffdfd,#ffffff)] p-5 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-6">
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl" style={{ background: "color-mix(in srgb, var(--color-primary) 18%, transparent)" }} />
                        <div className="absolute left-10 top-0 h-28 w-28 rounded-full bg-white/60 blur-2xl" />
                    </div>

                    <div className="relative flex flex-col gap-4">
                        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                            <div className="min-w-0">
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm">
                                    <Warehouse className="h-3.5 w-3.5" />
                                    Inventory Workspace
                                </div>
                                <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                                    Inventory List
                                </h1>
                                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500 sm:text-[15px]">
                                    Monitor warehouse stock, review released quantities by branch, and update inventory from one refined workspace.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 rounded-[22px] border border-white/70 bg-white/80 px-4 py-3 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                            <div>
                                <p className="text-sm font-semibold text-slate-900">Inventory Summary</p>
                                <p className="mt-1 text-xs text-slate-500">Show or hide the Products, Stock Alerts, Released, and Scope cards.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowSummaryCards((prev) => !prev)}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                                aria-expanded={showSummaryCards}
                                aria-label={showSummaryCards ? "Hide inventory summary cards" : "Show inventory summary cards"}
                            >
                                {showSummaryCards ? "Hide" : "Show"}
                                <ChevronDown className={`h-4 w-4 transition ${showSummaryCards ? "rotate-180" : "rotate-0"}`} />
                            </button>
                        </div>

                        {showSummaryCards && (
                            <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                                {loading ? (
                                    Array.from({ length: 4 }).map((_, idx) => (
                                        <div key={idx} className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                            <div className="h-3 w-20 rounded-full bg-slate-100" />
                                            <div className="mt-3 h-8 w-16 rounded-full bg-slate-200" />
                                            <div className="mt-2 h-3 w-32 rounded-full bg-slate-100" />
                                        </div>
                                    ))
                                ) : (
                                    <>
                                        <div className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                <Package className="h-4 w-4" />
                                                Products
                                            </div>
                                            <p className="mt-3 text-2xl font-semibold text-slate-900">{filteredInventory.length}</p>
                                            <p className="mt-1 text-xs text-slate-500">Items in the current filtered view</p>
                                        </div>
                                        <div className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                <AlertCircle className="h-4 w-4" />
                                                Stock Alerts
                                            </div>
                                            <div className="mt-3 grid grid-cols-2 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                                                <div className="border-r border-slate-200 px-3 py-2.5">
                                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600">
                                                        <AlertCircle className="h-3.5 w-3.5" />
                                                        Out
                                                    </div>
                                                    <p className="mt-1 text-2xl font-semibold text-rose-700">{outOfStockCount}</p>
                                                </div>
                                                <div className="px-3 py-2.5">
                                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600">
                                                        <AlertCircle className="h-3.5 w-3.5" />
                                                        Low
                                                    </div>
                                                    <p className="mt-1 text-2xl font-semibold text-amber-700">{lowStockCount}</p>
                                                </div>
                                            </div>
                                            <p className="mt-2 text-xs text-slate-500">
                                                Low threshold: <span className="font-semibold text-slate-700">{LOW_STOCK_THRESHOLD}</span> in current scope
                                            </p>
                                        </div>
                                        <div className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                <Building2 className="h-4 w-4" />
                                                Released
                                            </div>
                                            <p className="mt-3 text-2xl font-semibold text-slate-900">{totalReleasedStock}</p>
                                            <p className="mt-1 text-xs text-slate-500">
                                                {shouldShowBranchTabs
                                                    ? "Released inventory in the current branch view"
                                                    : "Released inventory for the active branch"}
                                            </p>
                                        </div>
                                        <div className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                <Search className="h-4 w-4" />
                                                Scope
                                            </div>
                                            <p className={`mt-3 truncate text-xl font-semibold ${hasNoActiveBranches ? "text-rose-600" : "text-slate-900"}`}>
                                                {activeBranchLabel}
                                            </p>
                                            <p className={`mt-1 text-xs ${hasNoActiveBranches ? "text-rose-500" : "text-slate-500"}`}>
                                                {hasNoActiveBranches
                                                    ? "Activate at least one branch to view released inventory."
                                                    : (shouldShowBranchTabs ? "Active inventory view" : "Current active branch")}
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </section>

                <section className="sticky z-30 rounded-[20px] border border-slate-200/80 bg-white/95 p-1.5 shadow-[0_18px_60px_rgba(15,23,42,0.09)] backdrop-blur-md sm:rounded-[22px] sm:p-2 md:p-2.5 xl:rounded-[24px] xl:p-3" style={{ top: "var(--admin-sticky-offset, 0px)" }}>
                    <div className="flex min-w-0 flex-col gap-1.5 sm:gap-2">
                        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-1 sm:gap-1.5 md:gap-2 xl:grid-cols-[minmax(0,1fr)_220px_220px_auto] xl:gap-3">
                            <div className="min-w-0">
                                <SearchInput
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    onSuggestionSelect={(productName) => {
                                        setSearchTerm(productName);
                                        setCurrentPage(1);
                                    }}
                                    suggestions={inventorySuggestions}
                                    placeholder="Search"
                                    disabled={loading}
                                    className="group relative w-full max-w-none"
                                    inputClassName="!py-1.5 !pl-9 !pr-2 !text-xs sm:!py-2 md:!py-2.5 md:!text-sm xl:!py-3 xl:!pl-11 xl:!pr-4"
                                    iconClassName="!left-3 xl:!left-4"
                                />
                            </div>
                            <div className="min-w-0 xl:hidden">
                                <ReactSelectField
                                    className="w-[34px] sm:w-[36px] md:w-[38px]"
                                    icon={ListFilter}
                                    variant="icon"
                                    options={stockFilterOptions}
                                    value={stockFilterOptions.find((option) => option.value === stockFilter)}
                                    onChange={(option) => setStockFilter(option?.value || "all")}
                                    isSearchable={false}
                                    isDisabled={loading}
                                    ariaLabel="Stock filter"
                                    menuMinWidth={170}
                                />
                            </div>
                            <div className="min-w-0 xl:hidden">
                                <ReactSelectField
                                    className="w-[34px] sm:w-[36px] md:w-[38px]"
                                    icon={ArrowUpDown}
                                    variant="icon"
                                    options={stockSortOptions}
                                    value={stockSortOptions.find((option) => option.value === stockSort)}
                                    onChange={(option) => setStockSort(option?.value || "stock-asc")}
                                    isSearchable={false}
                                    isDisabled={loading}
                                    ariaLabel="Stock sort"
                                    menuMinWidth={180}
                                />
                            </div>
                            <div className="hidden min-w-0 xl:block">
                                <ReactSelectField
                                    options={stockFilterOptions}
                                    value={stockFilterOptions.find((option) => option.value === stockFilter)}
                                    onChange={(option) => setStockFilter(option?.value || "all")}
                                    isSearchable={false}
                                    ariaLabel="Stock filter"
                                />
                            </div>
                            <div className="hidden min-w-0 xl:block">
                                <ReactSelectField
                                    options={stockSortOptions}
                                    value={stockSortOptions.find((option) => option.value === stockSort)}
                                    onChange={(option) => setStockSort(option?.value || "stock-asc")}
                                    isSearchable={false}
                                    ariaLabel="Stock sort"
                                />
                            </div>
                            <div className="flex shrink-0 items-center justify-end">
                                <ViewToggle
                                    viewMode={viewMode}
                                    onToggle={() => setViewMode(viewMode === "table" ? "grid" : "table")}
                                    className="!h-[34px] !w-[34px] !min-w-[34px] !rounded-xl sm:!h-[36px] sm:!w-[36px] sm:!min-w-[36px] sm:!rounded-[13px] md:!h-[38px] md:!w-[38px] md:!min-w-[38px] md:!rounded-[14px] xl:!h-11 xl:!w-11 xl:!min-w-11 xl:!rounded-xl"
                                    iconSize={15}
                                />
                            </div>
                        </div>

                        {shouldShowBranchTabs ? (
                            <div className="overflow-x-auto">
                                <div className="inline-flex min-w-full gap-1.5 rounded-[18px] border border-slate-200 bg-slate-50/85 p-1 shadow-inner shadow-white/40 sm:gap-2 sm:rounded-[22px] sm:p-1.5">
                                    {branchTabs.map((branchTab) => {
                                        const isActive = selectedBranchId === branchTab.id;
                                        return (
                                            <button
                                                key={branchTab.id || "all-branches"}
                                                type="button"
                                                onClick={() => setSelectedBranchId(branchTab.id)}
                                                className={`rounded-[15px] px-3 py-1.5 text-left transition sm:rounded-[18px] sm:px-4 ${isActive
                                                    ? "text-white shadow-[0_14px_30px_rgba(15,23,42,0.16)]"
                                                    : "text-slate-600 hover:bg-white hover:text-slate-900"
                                                    }`}
                                                style={isActive ? { background: "linear-gradient(135deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 76%, black))" } : undefined}
                                            >
                                                {branchTab.type === "warehouse" ? (
                                                    <span className={`block whitespace-nowrap text-sm font-semibold ${isActive ? "text-white" : ""}`}>
                                                        {branchTab.label}
                                                    </span>
                                                ) : (
                                                    <span className="block min-w-[76px] sm:min-w-[96px]">
                                                        <span className={`hidden text-[9px] font-semibold uppercase tracking-[0.18em] leading-none sm:block ${isActive ? "text-white/75" : "text-slate-400"}`}>
                                                            Branch
                                                        </span>
                                                        <span className={`block whitespace-nowrap text-sm font-semibold leading-tight sm:mt-0.5 ${isActive ? "text-white" : "text-slate-900"}`}>
                                                            {branchTab.label}
                                                        </span>
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </section>

                <section className="rounded-[30px] border border-slate-200/80 bg-white/95 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.06)] max-[480px]:p-3 sm:p-5">
                    <div className="flex flex-col gap-4 max-[480px]:gap-3 min-[640px]:flex-row min-[640px]:items-center min-[640px]:justify-between">
                        <div className="flex min-w-0 items-start gap-3 min-[640px]:items-center">
                            <div
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm min-[640px]:h-9 min-[640px]:w-9 xl:h-10 xl:w-10"
                                style={{ background: "linear-gradient(135deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 78%, black))" }}
                            >
                                <ShoppingBag className="h-5 w-5 min-[640px]:h-4 min-[640px]:w-4 xl:h-5 xl:w-5" />
                            </div>
                            <div className="min-w-0 min-[640px]:shrink-0">
                                <div className="flex flex-wrap items-center gap-2 min-[640px]:flex-nowrap">
                                    <h2 className="text-lg font-semibold text-slate-950 min-[640px]:whitespace-nowrap">To-Buy Checklist</h2>
                                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold leading-none text-slate-500 min-[640px]:h-7">
                                        {restockItems.length} selected
                                    </span>
                                </div>
                                <p className="mt-1 max-w-[420px] truncate text-xs leading-5 text-slate-500 min-[640px]:hidden xl:block">
                                    Select warehouse-low items for the buying list.
                                </p>
                            </div>
                        </div>

                        <div className="flex shrink-0 flex-wrap items-center gap-2 max-[480px]:flex-nowrap max-[480px]:gap-1.5 min-[640px]:flex-nowrap min-[640px]:justify-end min-[640px]:gap-1.5 xl:gap-2">
                            <button
                                type="button"
                                onClick={addLowStockToRestockList}
                                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 max-[480px]:h-9 max-[480px]:w-9 max-[480px]:gap-0 max-[480px]:rounded-xl max-[480px]:px-0 min-[640px]:h-9 min-[640px]:w-9 min-[640px]:gap-0 min-[640px]:rounded-xl min-[640px]:px-0 xl:w-auto xl:gap-2 xl:rounded-2xl xl:px-3 xl:text-sm"
                                title="Add low warehouse items"
                            >
                                <PlusCircle className="h-4 w-4 min-[640px]:h-3.5 min-[640px]:w-3.5 xl:h-4 xl:w-4" />
                                <span className="max-[1279px]:sr-only xl:hidden">Add Low Items</span>
                                <span className="hidden xl:inline">Add Low Warehouse Items</span>
                            </button>
                            <button
                                type="button"
                                onClick={copyRestockText}
                                disabled={!restockItems.length}
                                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 max-[480px]:h-9 max-[480px]:w-9 max-[480px]:gap-0 max-[480px]:rounded-xl max-[480px]:px-0 min-[640px]:h-9 min-[640px]:w-9 min-[640px]:gap-0 min-[640px]:rounded-xl min-[640px]:px-0 xl:w-auto xl:gap-2 xl:rounded-2xl xl:px-3 xl:text-sm"
                                title="Copy"
                            >
                                <Clipboard className="h-4 w-4 min-[640px]:h-3.5 min-[640px]:w-3.5 xl:h-4 xl:w-4" />
                                <span className="max-[1279px]:sr-only">Copy</span>
                            </button>
                            <button
                                type="button"
                                onClick={shareRestockText}
                                disabled={!restockItems.length}
                                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 max-[480px]:h-9 max-[480px]:w-9 max-[480px]:gap-0 max-[480px]:rounded-xl max-[480px]:px-0 min-[640px]:h-9 min-[640px]:w-9 min-[640px]:gap-0 min-[640px]:rounded-xl min-[640px]:px-0 xl:w-auto xl:gap-2 xl:rounded-2xl xl:px-3 xl:text-sm"
                                title="Share"
                            >
                                <Share2 className="h-4 w-4 min-[640px]:h-3.5 min-[640px]:w-3.5 xl:h-4 xl:w-4" />
                                <span className="max-[1279px]:sr-only">Share</span>
                            </button>
                            <button
                                type="button"
                                onClick={downloadRestockPdf}
                                disabled={!restockItems.length}
                                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-2xl px-3 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 max-[480px]:h-9 max-[480px]:w-9 max-[480px]:gap-0 max-[480px]:rounded-xl max-[480px]:px-0 min-[640px]:h-9 min-[640px]:w-9 min-[640px]:gap-0 min-[640px]:rounded-xl min-[640px]:px-0 xl:w-auto xl:gap-2 xl:rounded-2xl xl:px-3 xl:text-sm"
                                title="Download PDF"
                                style={restockItems.length ? { background: "linear-gradient(135deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 78%, black))" } : undefined}
                            >
                                <FileText className="h-4 w-4 min-[640px]:h-3.5 min-[640px]:w-3.5 xl:h-4 xl:w-4" />
                                <span className="text-inherit max-[1279px]:sr-only">PDF</span>
                            </button>
                            {restockItems.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setRestockItemsById({})}
                                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 max-[480px]:h-9 max-[480px]:w-9 max-[480px]:rounded-xl min-[640px]:h-9 min-[640px]:w-9 min-[640px]:rounded-xl xl:h-10 xl:w-10 xl:rounded-2xl"
                                    aria-label="Clear to-buy checklist"
                                    title="Clear to-buy checklist"
                                >
                                    <Trash2 className="h-4 w-4 min-[640px]:h-3.5 min-[640px]:w-3.5 xl:h-4 xl:w-4" />
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setShowRestockChecklist((prev) => !prev)}
                                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 max-[480px]:h-9 max-[480px]:w-9 max-[480px]:gap-0 max-[480px]:rounded-xl max-[480px]:px-0 min-[640px]:h-9 min-[640px]:w-9 min-[640px]:gap-0 min-[640px]:rounded-xl min-[640px]:px-0 xl:w-auto xl:gap-2 xl:rounded-2xl xl:px-3 xl:text-sm"
                                title={showRestockChecklist ? "Hide" : "Show"}
                                aria-expanded={showRestockChecklist}
                                aria-label={showRestockChecklist ? "Collapse to-buy checklist" : "Expand to-buy checklist"}
                            >
                                <span className="max-[1279px]:sr-only">{showRestockChecklist ? "Hide" : "Show"}</span>
                                <ChevronDown className={`h-4 w-4 transition min-[640px]:h-3.5 min-[640px]:w-3.5 xl:h-4 xl:w-4 ${showRestockChecklist ? "rotate-180" : "rotate-0"}`} />
                            </button>
                        </div>
                    </div>

                    {showRestockChecklist && restockItems.length > 0 ? (
                        <div className="mt-4 max-h-[260px] overflow-y-auto rounded-2xl border border-slate-100 bg-slate-50/35 p-1.5 pr-2 md:max-h-[300px] xl:max-h-[330px]">
                            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                            {restockItems.map(({ item, buyQty }, index) => {
                                const itemId = String(item.id);
                                const currentStock = getScopedStock(item, selectedBranchId);
                                const warehouseStock = getWarehouseStock(item);
                                const priority = getChecklistActionLabel(item, selectedBranchId);
                                return (
                                    <div key={itemId} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex min-w-0 items-start gap-2">
                                                <span
                                                    className="mt-0.5 inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white shadow-sm"
                                                    style={{ background: "linear-gradient(135deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 78%, black))" }}
                                                >
                                                    {index + 1}
                                                </span>
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-semibold text-slate-950">{item.product_name}</p>
                                                    <p className="mt-1 text-xs text-slate-500">
                                                        Current: {currentStock} | Warehouse: {warehouseStock}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => toggleRestockItem(item)}
                                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50"
                                                aria-label={`Remove ${item.product_name} from to-buy checklist`}
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                        <div className="mt-3 flex items-center justify-between gap-3">
                                            <label className="flex min-w-0 flex-1 items-center gap-2 text-xs font-semibold text-slate-500">
                                                Buy
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={buyQty}
                                                    onChange={(event) => updateRestockQty(itemId, event.target.value)}
                                                    className="h-9 w-20 rounded-xl border border-slate-200 bg-white px-2 text-center text-sm font-semibold text-slate-900 shadow-sm outline-none transition focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_18%,transparent)]"
                                                />
                                                <span className="truncate">{item.unit_type || "units"}</span>
                                            </label>
                                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                                                priority === "Urgent"
                                                    ? "bg-rose-50 text-rose-700 ring-1 ring-rose-100"
                                                    : priority === "Soon"
                                                    ? "bg-amber-50 text-amber-700 ring-1 ring-amber-100"
                                                    : priority === "Reorder"
                                                    ? "bg-orange-50 text-orange-700 ring-1 ring-orange-100"
                                                    : priority === "Transfer"
                                                    ? "bg-sky-50 text-sky-700 ring-1 ring-sky-100"
                                                    : "bg-slate-100 text-slate-600 ring-1 ring-slate-200"
                                            }`}>
                                                {priority}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                            </div>
                        </div>
                    ) : showRestockChecklist ? (
                        <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-5 text-sm text-slate-500">
                            Select items from the inventory list or add all low-stock items in the current view.
                        </div>
                    ) : null}
                </section>

                <section className="p-0">
                    {error ? (
                        <EmptyState
                            icon={Package}
                            title="Failed to Load Inventory"
                            message="Check your internet connection and try again."
                            onRetry={loadInventory}
                            retryLabel="Reload"
                        />
                    ) : loading ? (
                        <InventorySkeleton viewMode={viewMode} />
                    ) : currentInventory.length === 0 ? (
                        <EmptyState
                            icon={Package}
                            title="No Inventory Found"
                            message={searchTerm ? "No inventory matches your search." : "No inventory available yet."}
                        />
                    ) : viewMode === "table" ? (
                        <InventoryTable
                            inventories={currentInventory}
                            onAdjust={handleAdjust}
                            uploadsBaseURL={uploadsBaseURL}
                            onEditStock={handleEditStock}
                            selectedBranchId={selectedBranchId}
                            hideActions={hasNoActiveBranches}
                            onPreviewImage={setPreviewProduct}
                            selectedRestockIds={selectedRestockIds}
                            onToggleRestock={toggleRestockItem}
                        />
                    ) : (
                        <InventoryGrid
                            inventories={currentInventory}
                            onAdjust={handleAdjust}
                            uploadsBaseURL={uploadsBaseURL}
                            selectedBranchId={selectedBranchId}
                            selectedRestockIds={selectedRestockIds}
                            onToggleRestock={toggleRestockItem}
                            onEdit={(inv) => {
                                setSelectedInventory(inv);
                                setEditModalOpen(true);
                            }}
                        />
                    )}
                </section>

                {!error && (
                    <div className="flex items-center justify-between gap-3 overflow-x-auto pb-10 text-xs whitespace-nowrap">
                        <div className="shrink-0 text-gray-500">
                            Showing {filteredInventory.length === 0 ? 0 : startIndex + 1}
                            -{Math.min(startIndex + inventoriesPerPage, filteredInventory.length)} of {filteredInventory.length}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <button
                                type="button"
                                className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                                disabled={safePage === 1}
                            >
                                Prev
                            </button>
                            <div className="text-xs text-gray-600">
                                Page {safePage} of {totalPages}
                            </div>
                            <button
                                type="button"
                                className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                                disabled={safePage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <EditStockModal
                isOpen={editModalOpen}
                onClose={handleCloseModal}
                inventory={selectedInventory}
                onSave={handleSaveStock}
                selectedBranchId={selectedBranchId}
            />
            <ProductImagePreviewModal
                open={Boolean(previewProduct)}
                onClose={() => setPreviewProduct(null)}
                product={previewProduct ? {
                    ...previewProduct,
                    name: previewProduct.product_name || previewProduct.name,
                } : null}
            />
        </div>
    );
};

export default InventoryPage;
